import {
  ROUTE_CONFIG,
  ROUTE_CONFIG_IMPORT,
  ROUTE_CURSOR_AGENT,
  ROUTE_DAILY,
  ROUTE_CHESS,
  ROUTE_EAR_TRAINING,
  ROUTE_TOOLS_ENCRYPT,
  ROUTE_NEWS,
  ROUTE_WEATHER,
} from '../constants/routes';
import { appAlert } from '../utils/appDialog';
import { appendChessGameAnalysisToJournal } from './chessGameJournal';
import { CHESS_JOURNAL_PATH, DASHBOARD_HTML_PATH } from './constants';
import type { AppContext } from './appContext';
import { promptAndAddQuickListItem } from './quickListHandlers';

export function revokeDashboardBlob(ctx: AppContext): void {
  if (ctx.dashboardBlobUrl) {
    URL.revokeObjectURL(ctx.dashboardBlobUrl);
    ctx.dashboardBlobUrl = null;
  }
}

/** Morning email HTML (inlined CSS) in an isolated iframe. */
export function renderDailyDashboard(ctx: AppContext, mainEl: HTMLElement, html: string): void {
  revokeDashboardBlob(ctx);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  ctx.dashboardBlobUrl = URL.createObjectURL(blob);
  mainEl.innerHTML = `
      <div class="daily-dashboard">
        <header class="daily-dashboard-header">
          <h2 class="file-title">Daily Update</h2>
          <p class="daily-dashboard-note">English property briefing. Updates after each successful daily workflow run. Spanish practice is on the Español page.</p>
        </header>
        <iframe class="daily-dashboard-frame" title="Daily briefing" sandbox="allow-popups allow-popups-to-escape-sandbox" src="${ctx.dashboardBlobUrl}"></iframe>
      </div>`;
}

/** Default landing: AI Agent chat. */
export async function showAppHome(
  ctx: AppContext,
  sidebarEl: HTMLElement,
  mainEl: HTMLElement
): Promise<void> {
  await showCursorAgent(ctx, sidebarEl, mainEl);
}

export async function showEncryptLinkTool(
  ctx: AppContext,
  sidebarEl: HTMLElement,
  mainEl: HTMLElement
): Promise<void> {
  if (ctx.fileView.hasUnsavedChanges()) {
    void appAlert('Save or discard changes before opening Tools.');
    return;
  }
  revokeDashboardBlob(ctx);
  ctx.shareEncryptToolView.render(mainEl);
  ctx.sidebar.setActivePath(ROUTE_TOOLS_ENCRYPT);
  ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
}

export async function showConfig(
  ctx: AppContext,
  sidebarEl: HTMLElement,
  mainEl: HTMLElement
): Promise<void> {
  if (ctx.fileView.hasUnsavedChanges()) {
    void appAlert('Save or discard changes before opening Config.');
    return;
  }
  revokeDashboardBlob(ctx);
  ctx.configView.render(mainEl, {
    onOpenImport: () => {
      void showConfigImport(ctx, sidebarEl, mainEl);
    },
  });
  ctx.sidebar.setActivePath(ROUTE_CONFIG);
  ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
}

export async function showConfigImport(
  ctx: AppContext,
  sidebarEl: HTMLElement,
  mainEl: HTMLElement
): Promise<void> {
  if (ctx.fileView.hasUnsavedChanges()) {
    void appAlert('Save or discard changes before importing settings.');
    return;
  }
  revokeDashboardBlob(ctx);
  ctx.configImportView.render(mainEl, {
    onBack: () => {
      void showConfig(ctx, sidebarEl, mainEl);
    },
    onApplied: () => {
      void appAlert(
        'Settings were written to localStorage. Reload the page if GitHub sign-in or sidebar state looks wrong.',
        'Import complete'
      );
    },
  });
  ctx.sidebar.setActivePath(ROUTE_CONFIG_IMPORT);
  ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
}

export async function showWeather(
  ctx: AppContext,
  sidebarEl: HTMLElement,
  mainEl: HTMLElement
): Promise<void> {
  if (ctx.fileView.hasUnsavedChanges()) {
    void appAlert('Save or discard changes before opening Weather.');
    return;
  }
  revokeDashboardBlob(ctx);
  ctx.weatherView.render(mainEl);
  ctx.sidebar.setActivePath(ROUTE_WEATHER);
  ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
}

export async function showNews(
  ctx: AppContext,
  sidebarEl: HTMLElement,
  mainEl: HTMLElement
): Promise<void> {
  if (ctx.fileView.hasUnsavedChanges()) {
    void appAlert('Save or discard changes before opening News.');
    return;
  }
  revokeDashboardBlob(ctx);
  ctx.newsView.reloadConfig = () => ctx.newsFeedConfig.load();
  ctx.newsView.render(mainEl);
  ctx.sidebar.setActivePath(ROUTE_NEWS);
  ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
}

export async function showEarTraining(
  ctx: AppContext,
  sidebarEl: HTMLElement,
  mainEl: HTMLElement
): Promise<void> {
  if (ctx.fileView.hasUnsavedChanges()) {
    void appAlert('Save or discard changes before opening Ear Training.');
    return;
  }
  revokeDashboardBlob(ctx);
  ctx.earTrainingView.render(mainEl);
  ctx.sidebar.setActivePath(ROUTE_EAR_TRAINING);
  ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
}

export async function showCursorAgent(
  ctx: AppContext,
  sidebarEl: HTMLElement,
  mainEl: HTMLElement
): Promise<void> {
  if (ctx.fileView.hasUnsavedChanges()) {
    void appAlert('Save or discard changes before opening AI Agent.');
    return;
  }
  revokeDashboardBlob(ctx);
  ctx.cursorAgentView.render(mainEl, {
    onQuickGrocery: () => promptAndAddQuickListItem(ctx, 'grocery'),
    onQuickTodo: () => promptAndAddQuickListItem(ctx, 'todo'),
  });
  ctx.sidebar.setActivePath(ROUTE_CURSOR_AGENT);
  ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
}

export async function showChess(
  ctx: AppContext,
  sidebarEl: HTMLElement,
  mainEl: HTMLElement
): Promise<void> {
  if (ctx.fileView.hasUnsavedChanges()) {
    void appAlert('Save or discard changes before opening Chess.');
    return;
  }
  revokeDashboardBlob(ctx);
  ctx.chessView.render(mainEl, {
    chessGamesPath: CHESS_JOURNAL_PATH,
    saveAnalyzedGame: opts => appendChessGameAnalysisToJournal(ctx, opts),
    onJournalSaved: () => {
      ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
    },
  });
  ctx.sidebar.setActivePath(ROUTE_CHESS);
  ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
}

export async function showDailyUpdate(
  ctx: AppContext,
  sidebarEl: HTMLElement,
  mainEl: HTMLElement
): Promise<void> {
  if (ctx.fileView.hasUnsavedChanges()) {
    void appAlert('Save or discard changes before opening Daily Update.');
    return;
  }
  if (!ctx.dailyDashboardAvailable) {
    await ctx.refreshDailyDashboardAvailability();
    ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
    if (!ctx.dailyDashboardAvailable) return;
  }
  try {
    const { content } = await ctx.github.readFile(DASHBOARD_HTML_PATH);
    renderDailyDashboard(ctx, mainEl, content);
    ctx.sidebar.setActivePath(ROUTE_DAILY);
    ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
  } catch {
    mainEl.innerHTML = `
        <div class="empty-state">
          <div class="empty-icon" aria-hidden="true">📭</div>
          <p>No daily briefing yet. The workflow publishes <code>database/daily-dashboard.html</code> after each successful daily run.</p>
        </div>`;
    ctx.sidebar.setActivePath(ROUTE_DAILY);
    ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
  }
}
